import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UploadCloud, Image as ImageIcon, Film, CheckCircle2, Clock, Loader2, FolderOpen, Check, UserCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import {
  MediaAsset,
  MediaAssetType,
  assetDisplayName,
  backfillMissingThumbnails,
  fetchOrgMediaAssets,
  galleryListStatus,
  galleryThumbSrc,
  getUserOrgId,
  isAvatarAsset,
  isImageAsset,
  isMediaApproved,
  normalizeGalleryFile,
  uploadToGallery,
  urlToDataUrl,
} from '../lib/mediaGallery';
import { useImagePurposePrompt } from '../hooks/useImagePurposePrompt';

export type GalleryPickResult = {
  asset: MediaAsset;
  /** Data URL for images (ready for CreationPanel frames). Empty for videos. */
  dataUrl?: string;
};

type AcceptMode = 'image' | 'video' | 'both';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (result: GalleryPickResult) => void | Promise<void>;
  accept?: AcceptMode;
  title?: string;
  /** When true, local upload also saves to gallery before selecting. */
  saveUploadsToGallery?: boolean;
  /** Click to toggle several items, then confirm. Upload also accepts many files. */
  multiple?: boolean;
  /** Called as soon as the user picks an item, before the file is fetched. */
  onSelectStart?: (asset: MediaAsset) => void;
}

async function waitIfPromise(value: unknown) {
  if (value && typeof (value as Promise<unknown>).then === 'function') {
    await value;
  }
}

export default function GalleryMediaPicker({
  open,
  onClose,
  onSelect,
  accept = 'both',
  title = 'Galeria da organização',
  saveUploadsToGallery = true,
  multiple = false,
  onSelectStart,
}: Props) {
  const { user } = useAuth();
  const { role } = useOrg();
  const fileRef = useRef<HTMLInputElement>(null);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | MediaAssetType | 'avatar'>(
    accept === 'image' ? 'image' : accept === 'video' ? 'video' : 'all',
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const { promptPurpose, modal: purposeModal } = useImagePurposePrompt();

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const orgId = await getUserOrgId(user.id);
      if (!orgId) throw new Error('Organização não encontrada.');
      const assetType = filter === 'avatar' || filter === 'all' ? (filter === 'avatar' ? 'video' : 'all') : filter;
      const rows = await fetchOrgMediaAssets(orgId, { assetType, status: galleryListStatus(role) });
      setAssets(filter === 'avatar' ? rows.filter(isAvatarAsset) : rows);
      void backfillMissingThumbnails(orgId, rows, (id, thumbnailUrl) => {
        setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, thumbnail_url: thumbnailUrl } : a)));
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar galeria.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, filter, role]);

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const emitAsset = async (asset: MediaAsset) => {
    if (isImageAsset(asset)) {
      try {
        const dataUrl = await urlToDataUrl(asset.video_url);
        await waitIfPromise(onSelect({ asset, dataUrl }));
        return;
      } catch {
        await waitIfPromise(onSelect({ asset, dataUrl: asset.video_url }));
        return;
      }
    }
    await waitIfPromise(onSelect({ asset }));
  };

  const handlePickAsset = async (asset: MediaAsset) => {
    if (multiple) {
      setSelectedIds((prev) => (
        prev.includes(asset.id) ? prev.filter((id) => id !== asset.id) : [...prev, asset.id]
      ));
      return;
    }
    setBusyId(asset.id);
    setError(null);
    try {
      onSelectStart?.(asset);
      await emitAsset(asset);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Falha ao anexar mídia.');
    } finally {
      setBusyId(null);
    }
  };

  const confirmSelection = async () => {
    const chosen = assets.filter((a) => selectedIds.includes(a.id));
    if (!chosen.length) return;
    setConfirming(true);
    setError(null);
    try {
      for (const asset of chosen) {
        setBusyId(asset.id);
        onSelectStart?.(asset);
        await emitAsset(asset);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Falha ao anexar mídia.');
    } finally {
      setBusyId(null);
      setConfirming(false);
    }
  };

  const processFiles = async (files: File[]) => {
    const matching = files.filter((file) => {
      if (accept === 'image') return file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(file.name);
      if (accept === 'video') return file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(file.name);
      return file.type.startsWith('image/') || file.type.startsWith('video/')
        || /\.(jpe?g|png|webp|gif|heic|heif|avif|mp4|webm|mov|m4v)$/i.test(file.name);
    });
    if (!user?.id) {
      setError('Faça login para enviar arquivos.');
      return;
    }
    if (!matching.length) {
      setError('Selecione uma imagem (jpg, png, webp) ou um vídeo (mp4, webm).');
      return;
    }
    setError(null);
    try {
      const orgId = saveUploadsToGallery ? await getUserOrgId(user.id) : '';
      if (saveUploadsToGallery && !orgId) throw new Error('Organização não encontrada.');
      const images = matching.filter((file) => {
        const n = normalizeGalleryFile(file);
        return n.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(n.name);
      });
      let imageIndex = 0;
      let attached = 0;
      for (const raw of matching) {
        const file = normalizeGalleryFile(raw);
        const isImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(file.name);
        let purpose: 'avatar' | 'photo' | undefined;
        if (isImage) {
          const previewUrl = URL.createObjectURL(file);
          purpose = await promptPurpose({
            preview: previewUrl,
            label: file.name.replace(/\.[^.]+$/, ''),
            remaining: images.length - imageIndex,
          }) || undefined;
          URL.revokeObjectURL(previewUrl);
          imageIndex += 1;
          if (!purpose) {
            setError('Escolha se a imagem é Avatar (rosto) ou Foto (cena) para anexar.');
            continue;
          }
        }
        setUploading(true);
        if (saveUploadsToGallery && orgId) {
          const asset = await uploadToGallery(orgId, file, { purpose });
          setAssets((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
          onSelectStart?.(asset);
          await emitAsset(asset);
          attached += 1;
        } else {
          const localUrl = isImage
            ? await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(new Error('Falha ao ler arquivo.'));
                reader.readAsDataURL(file);
              })
            : URL.createObjectURL(file);
          await waitIfPromise(onSelect({
            asset: {
              id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              org_id: '',
              video_url: localUrl,
              asset_type: isImage ? 'image' : 'video',
              title: file.name.replace(/\.[^.]+$/, ''),
              status: 'approved',
              generation_id: null,
              generation_context: purpose ? `purpose:${purpose}` : null,
              thumbnail_url: isImage ? localUrl : null,
              created_at: new Date().toISOString(),
            },
            dataUrl: isImage ? localUrl : undefined,
          }));
          attached += 1;
        }
      }
      if (attached) onClose();
    } catch (err: any) {
      setError(err.message || 'Falha no upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleLocalUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    await processFiles(files);
  };

  const busy = uploading || confirming || !!busyId;

  return createPortal(
    <>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(12px, 3vw, 32px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onDragEnter={(e) => {
          if (![...e.dataTransfer.types].includes('Files')) return;
          e.preventDefault();
          setDropOver(true);
        }}
        onDragOver={(e) => {
          if (![...e.dataTransfer.types].includes('Files')) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDropOver(false);
          void processFiles(Array.from(e.dataTransfer.files || []));
        }}
        style={{
          width: 'min(1080px, 100%)',
          height: 'min(82vh, 760px)',
          maxHeight: '90vh',
          background: 'rgba(22,24,30,0.98)',
          border: dropOver ? '1px solid #818cf8' : '1px solid rgba(255,255,255,0.1)',
          borderRadius: 20, display: 'flex', flexDirection: 'column',
          boxShadow: '0 28px 90px rgba(0,0,0,0.6)', overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.15rem 1.4rem', borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 650, fontSize: '1.15rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FolderOpen size={22} style={{ color: '#818cf8' }} /> {title}
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
              {multiple
                ? 'Selecione várias mídias e clique em Adicionar — ou envie vários arquivos no upload'
                : 'Anexe da galeria ou faça upload (salvo automaticamente na galeria)'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '1rem 1.4rem', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)', flexShrink: 0 }}>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3 }}>
            {([
              { id: 'all' as const, label: 'Todos' },
              { id: 'image' as const, label: 'Foto', icon: <ImageIcon size={12} /> },
              { id: 'video' as const, label: 'Vídeo', icon: <Film size={12} /> },
              ...(accept !== 'image' ? [{ id: 'avatar' as const, label: 'Avatar', icon: <UserCircle size={12} /> }] : []),
            ]).map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                style={{
                  padding: '0.45rem 0.9rem', border: 'none', borderRadius: 8, fontSize: '0.8rem',
                  background: filter === f.id ? 'rgba(99,102,241,0.35)' : 'transparent',
                  color: '#fff', cursor: 'pointer', fontWeight: filter === f.id ? 600 : 400,
                  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                {f.icon} {f.label}
              </button>
            ))}
          </div>
          {multiple && assets.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedIds(assets.map((a) => a.id))}
              disabled={busy}
              style={{
                background: 'none', border: 'none', color: '#a5b4fc', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              Selecionar todas
            </button>
          )}
          <label
            style={{
              position: 'relative',
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8,
              padding: '0.65rem 1.15rem', borderRadius: 10, border: '1px solid rgba(99,102,241,0.4)',
              background: 'rgba(99,102,241,0.2)', color: '#c7d2fe', fontSize: '0.85rem',
              fontWeight: 600, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {uploading ? <Loader2 size={16} className="spin" /> : <UploadCloud size={16} />}
            {uploading ? 'Enviando…' : multiple ? 'Upload de várias' : 'Upload novo'}
            <input
              ref={fileRef}
              type="file"
              multiple={multiple}
              disabled={busy}
              onChange={handleLocalUpload}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'inherit',
                fontSize: 16,
              }}
            />
          </label>
        </div>

        {error && (
          <div style={{ margin: '0.85rem 1.4rem 0', padding: '0.75rem 1rem', borderRadius: 10, background: 'rgba(239,68,68,0.12)', color: '#fca5a5', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.4rem 1.5rem', minHeight: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4.5rem 1rem', color: 'var(--text-muted)', fontSize: '0.95rem' }}>
              <Loader2 size={28} style={{ margin: '0 auto 0.75rem', display: 'block' }} className="spin" />
              Carregando…
            </div>
          ) : assets.length === 0 ? (
            <div style={{
              textAlign: 'center', minHeight: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: '3.5rem 1.5rem', color: 'var(--text-muted)',
            }}>
              <ImageIcon size={56} style={{ opacity: 0.35, marginBottom: '1rem' }} />
              <p style={{ fontSize: '1.05rem', marginBottom: 8, color: 'rgba(255,255,255,0.75)', fontWeight: 550 }}>Nenhuma mídia nesta galeria</p>
              <p style={{ fontSize: '0.88rem', opacity: 0.75, marginBottom: '1.25rem', maxWidth: 360, lineHeight: 1.45 }}>
                Faça upload agora ou gere conteúdos no Painel de Criação
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '0.7rem 1.25rem', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff', fontSize: '0.9rem', fontWeight: 650,
                  cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                }}
              >
                <UploadCloud size={18} /> Upload novo
              </button>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '1rem',
            }}>
              {assets.map((asset) => {
                const image = isImageAsset(asset);
                const selected = selectedIds.includes(asset.id);
                const itemBusy = busyId === asset.id;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => handlePickAsset(asset)}
                    disabled={busy && !multiple}
                    style={{
                      textAlign: 'left', padding: 0,
                      border: selected ? '2px solid #818cf8' : '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 14, overflow: 'hidden',
                      cursor: itemBusy ? 'wait' : 'pointer',
                      background: selected ? 'rgba(99,102,241,0.16)' : 'rgba(255,255,255,0.03)',
                      fontFamily: 'inherit', color: 'inherit',
                      boxShadow: selected ? '0 0 0 1px rgba(129,140,248,0.35)' : undefined,
                    }}
                  >
                    <div style={{
                      height: 148, background: '#0a0b0e', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden',
                    }}>
                      {image ? (
                        <img src={galleryThumbSrc(asset)} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <video src={asset.video_url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      <span style={{
                        position: 'absolute', top: 6, left: 6, fontSize: '0.6rem', fontWeight: 600,
                        background: 'rgba(0,0,0,0.65)', padding: '2px 7px', borderRadius: 999,
                        display: 'flex', alignItems: 'center', gap: 3, color: '#fff',
                      }}>
                        {isAvatarAsset(asset) ? <UserCircle size={10} /> : image ? <ImageIcon size={10} /> : <Film size={10} />}
                        {isAvatarAsset(asset) ? 'Avatar' : image ? 'Foto' : 'Vídeo'}
                      </span>
                      {multiple && (
                        <span style={{
                          position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: 6,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: selected ? '#6366f1' : 'rgba(0,0,0,0.55)',
                          border: selected ? 'none' : '1px solid rgba(255,255,255,0.35)',
                          color: '#fff',
                        }}>
                          {selected && <Check size={14} />}
                        </span>
                      )}
                      {itemBusy && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Loader2 size={20} color="#fff" className="spin" />
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '0.55rem 0.7rem' }}>
                      <div
                        title={assetDisplayName(asset)}
                        style={{
                          fontSize: '0.78rem',
                          fontWeight: 550,
                          color: '#fff',
                          lineHeight: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {assetDisplayName(asset)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {isMediaApproved(asset)
                          ? <><CheckCircle2 size={9} color="#10b981" /> Aprovado</>
                          : <><Clock size={9} color="#f59e0b" /> Pendente</>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {multiple && (
          <div style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0.9rem 1.4rem',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(0,0,0,0.25)',
          }}>
            <span style={{ fontSize: '0.85rem', color: selectedIds.length ? '#e0e7ff' : 'var(--text-muted)' }}>
              {selectedIds.length === 0
                ? 'Nenhuma mídia selecionada'
                : `${selectedIds.length} selecionada${selectedIds.length > 1 ? 's' : ''}`}
            </span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                disabled={busy}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' }}
              >
                Limpar
              </button>
            )}
            <button
              type="button"
              onClick={confirmSelection}
              disabled={!selectedIds.length || busy}
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '0.65rem 1.2rem',
                borderRadius: 10,
                border: 'none',
                background: selectedIds.length ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontSize: '0.88rem',
                fontWeight: 650,
                cursor: selectedIds.length && !busy ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                opacity: selectedIds.length ? 1 : 0.55,
              }}
            >
              {confirming ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
              Adicionar
            </button>
          </div>
        )}
        {dropOver && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 5,
            background: 'rgba(99,102,241,0.22)',
            border: '2px dashed #818cf8',
            borderRadius: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#e0e7ff', fontSize: '1.05rem', fontWeight: 650,
            pointerEvents: 'none',
          }}>
            Solte as imagens para enviar
          </div>
        )}
      </div>
      <style>{`.spin { animation: spin 0.8s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
    {purposeModal}
    </>,
    document.body,
  );
}
